import React, { useState, useEffect, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// Error boundary to prevent white screen crashes
class ProductErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('ProductManager crash:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
          <div className="text-center p-8">
            <h2 className="text-xl font-bold text-foreground mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-4">{this.state.error?.message}</p>
            <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg" onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}>Reload Page</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Trash2, Save, ArrowLeft, Settings, Package, DollarSign, Users, X, Scale, Percent, Layers, Search } from 'lucide-react';
import PackageBuilder from './PackageBuilder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { ADMIN_CATEGORIES, SUB_CATEGORY_LABELS } from '../utils/planningConstants';

const PRICING_MODELS = [
  { value: 'flat_rate', label: 'Flat Rate', description: 'One-time fixed price' },
  { value: 'per_hour', label: 'Per Hour', description: 'Charged by the hour' },
  { value: 'per_person', label: 'Per Person', description: 'Charged per guest' },
  { value: 'per_hour_per_person', label: 'Per Hour & Per Person', description: 'Both hourly and per guest' },
  { value: 'per_oz', label: 'Per Ounce (oz)', description: 'Charged per ounce (food/catering)' },
  { value: 'per_lb', label: 'Per Pound (lb)', description: 'Charged per pound (food/catering)' },
  { value: 'per_unit', label: 'Per Unit/Tray/Piece', description: 'Charged per unit, tray, or piece' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

function AdminProductManagerInner() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  // Admin Settings
  const [adminSettings, setAdminSettings] = useState({
    admin_service_fee_percent: 15,
    min_booking_notice_hours: 24,
    max_event_guests: 1000
  });
  const [cityFilter, setCityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Supplier Hub for preferred supplier selection
  const [availableVendors, setAvailableVendors] = useState([]);
  const [preferredVendors, setPreferredVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [providerCalendar, setProviderCalendar] = useState(null);
  
  // Product Form State
  const [formData, setFormData] = useState({
    name: '',
    category: 'food',
    sub_category: '',
    description: '',
    short_description: '',
    pricing_model: 'flat_rate',
    base_price: 0,
    min_hours: null,
    max_hours: null,
    min_guests: null,
    max_guests: null,
    min_quantity: null,
    pricing_unit_label: '',
    supplier_commission_rate: 0.15,
    customer_markup_rate: 0.18,
    host_name: '',
    host_business_name: '',
    host_email: '',
    host_phone: '',
    service_location: '',
    service_zip_code: '',
    service_radius_miles: 25,
    covered_zip_codes: '',
    image_url: '',
    what_to_expect: '',
    whats_included: [],
    host_requirements: [],
    option_groups: [],
    // Drop-off options
    has_dropoff_option: false,
    dropoff_price: '',
    dropoff_delivery_radius: '',
    dropoff_minimum_order: '',
    availability: {
      days_of_week: [0, 1, 2, 3, 4, 5, 6],
      start_time: '08:00',
      end_time: '22:00',
      min_notice_hours: 24,
      blackout_dates: []
    }
  });

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch products
  useEffect(() => {
    fetchProducts();
    fetchAdminSettings();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/services/products?active_only=false`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API}/vendors/marketplace-list`);
      setAvailableVendors(res.data.vendors || []);
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  };

  const fetchProviderCalendar = async () => {
    try {
      const provRes = await axios.get(`${API}/providers/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (provRes.data) {
        setProviderCalendar(provRes.data);
      }
    } catch { /* no provider */ }
  };

  const fetchPreferredVendors = async (serviceId) => {
    try {
      const res = await axios.get(`${API}/services/products/${serviceId}/preferred-vendors`);
      setPreferredVendors(res.data || []);
    } catch (err) {
      console.error('Failed to fetch preferred vendors:', err);
      setPreferredVendors([]);
    }
  };

  const addPreferredVendor = async (vendorId) => {
    if (!editingProduct) return;
    try {
      await axios.post(`${API}/services/products/${editingProduct.service_id}/preferred-vendors`,
        { vendor_id: vendorId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Preferred vendor added (pending acceptance)');
      fetchPreferredVendors(editingProduct.service_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add vendor');
    }
  };

  const removePreferredVendor = async (vendorId) => {
    if (!editingProduct) return;
    try {
      await axios.delete(`${API}/services/products/${editingProduct.service_id}/preferred-vendors/${vendorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vendor removed');
      fetchPreferredVendors(editingProduct.service_id);
    } catch (err) {
      toast.error('Failed to remove vendor');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      // When category changes, clear sub_category if it's no longer valid
      if (field === 'category' && value !== prev.category) {
        const next = ADMIN_CATEGORIES.find(c => c.id === value);
        const validSubs = next?.subCategories || [];
        const keepSub = validSubs.includes(prev.sub_category) ? prev.sub_category : '';
        return { ...prev, category: value, sub_category: keepSub };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleAvailabilityChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      availability: { ...prev.availability, [field]: value }
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const days = prev.availability.days_of_week;
      const newDays = days.includes(day)
        ? days.filter(d => d !== day)
        : [...days, day].sort();
      return {
        ...prev,
        availability: { ...prev.availability, days_of_week: newDays }
      };
    });
  };

  // Option group management
  const addOptionGroup = () => {
    setFormData(prev => ({
      ...prev,
      option_groups: [
        ...prev.option_groups,
        {
          option_id: `opt_${Date.now()}`,
          name: '',
          description: '',
          required: false,
          multiple_select: false,
          min_selections: 0,
          max_selections: null,
          choices: []
        }
      ]
    }));
  };

  const updateOptionGroup = (index, field, value) => {
    setFormData(prev => {
      const groups = [...prev.option_groups];
      groups[index] = { ...groups[index], [field]: value };
      return { ...prev, option_groups: groups };
    });
  };

  const removeOptionGroup = (index) => {
    setFormData(prev => ({
      ...prev,
      option_groups: prev.option_groups.filter((_, i) => i !== index)
    }));
  };

  const addChoice = (groupIndex) => {
    setFormData(prev => {
      const groups = [...prev.option_groups];
      groups[groupIndex].choices.push({
        choice_id: `ch_${Date.now()}`,
        name: '',
        description: '',
        price_modifier: 0,
        is_default: false
      });
      return { ...prev, option_groups: groups };
    });
  };

  const updateChoice = (groupIndex, choiceIndex, field, value) => {
    setFormData(prev => {
      const groups = [...prev.option_groups];
      groups[groupIndex].choices[choiceIndex] = {
        ...groups[groupIndex].choices[choiceIndex],
        [field]: value
      };
      return { ...prev, option_groups: groups };
    });
  };

  const removeChoice = (groupIndex, choiceIndex) => {
    setFormData(prev => {
      const groups = [...prev.option_groups];
      groups[groupIndex].choices = groups[groupIndex].choices.filter((_, i) => i !== choiceIndex);
      return { ...prev, option_groups: groups };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        // Strip empty list rows so we don't ship blank bullets to the public listing
        whats_included: (formData.whats_included || []).map(s => (s || '').trim()).filter(Boolean),
        host_requirements: (formData.host_requirements || []).map(s => (s || '').trim()).filter(Boolean),
        what_to_expect: (formData.what_to_expect || '').trim(),
        covered_zip_codes: Array.isArray(formData.covered_zip_codes)
          ? formData.covered_zip_codes
          : formData.covered_zip_codes
            ? formData.covered_zip_codes.split(',').map(z => z.trim()).filter(Boolean)
            : [],
        option_groups: (formData.option_groups || []).map(og => ({
          ...og,
          choices: (og.choices || []).map(c => ({
            ...c,
            price_modifier: parseFloat(c.price_modifier) || 0,
            flat_price: parseFloat(c.flat_price) || 0,
          }))
        })),
        base_price: parseFloat(formData.base_price) || 0,
        min_guests: parseInt(formData.min_guests) || null,
        max_guests: parseInt(formData.max_guests) || null,
        min_hours: parseInt(formData.min_hours) || null,
        max_hours: parseInt(formData.max_hours) || null,
        host_email: formData.host_email || null,
        dropoff_price: formData.dropoff_price ? parseFloat(formData.dropoff_price) : null,
        dropoff_delivery_radius: formData.dropoff_delivery_radius ? parseInt(formData.dropoff_delivery_radius) : null,
        dropoff_minimum_order: formData.dropoff_minimum_order ? parseFloat(formData.dropoff_minimum_order) : null,
      };
      
      if (editingProduct) {
        await axios.put(`${API}/services/products/${editingProduct.service_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated successfully');
      } else {
        const res = await axios.post(`${API}/services/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created successfully');
        // Auto-add to Eventism Catering Colorado provider
        try {
          const newServiceId = res.data?.service_id;
          if (newServiceId) {
            const provRes = await axios.get(`${API}/providers/me`, { headers: { Authorization: `Bearer ${token}` } });
            if (provRes.data?.provider_id) {
              const existingIds = provRes.data.service_ids || [];
              if (!existingIds.includes(newServiceId)) {
                await axios.put(`${API}/providers/${provRes.data.provider_id}`, {
                  service_ids: [...existingIds, newServiceId]
                }, { headers: { Authorization: `Bearer ${token}` } });
              }
            }
          }
        } catch (provErr) { console.error('Auto-link provider:', provErr); }
      }
      
      fetchProducts();
      resetForm();
    } catch (error) {
      console.error('Failed to save product:', error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || JSON.stringify(e)).join(', '));
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to save product');
      }
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    fetchVendors();
    fetchPreferredVendors(product.service_id);
    fetchProviderCalendar();
    setFormData({
      name: product.name || '',
      category: product.category || 'food',
      sub_category: product.sub_category || '',
      description: product.description || '',
      short_description: product.short_description || '',
      pricing_model: product.pricing_model || 'flat_rate',
      base_price: product.base_price || 0,
      min_hours: product.min_hours,
      max_hours: product.max_hours,
      min_guests: product.min_guests,
      max_guests: product.max_guests,
      min_quantity: product.min_quantity || null,
      pricing_unit_label: product.pricing_unit_label || '',
      supplier_commission_rate: product.supplier_commission_rate ?? 0.15,
      customer_markup_rate: product.customer_markup_rate ?? 0.18,
      host_name: product.host?.name || '',
      host_business_name: product.host?.business_name || '',
      host_email: product.host?.email || '',
      host_phone: product.host?.phone || '',
      service_location: product.service_location || '',
      service_zip_code: product.service_zip_code || '',
      service_radius_miles: product.service_radius_miles || 25,
      covered_zip_codes: Array.isArray(product.covered_zip_codes) ? product.covered_zip_codes.join(', ') : (product.covered_zip_codes || ''),
      image_url: product.image_url || '',
      what_to_expect: product.what_to_expect || '',
      whats_included: Array.isArray(product.whats_included) ? product.whats_included : [],
      host_requirements: Array.isArray(product.host_requirements) ? product.host_requirements : [],
      option_groups: (Array.isArray(product.option_groups) ? product.option_groups : []).map(og => {
        if (!og || typeof og !== 'object') return { option_id: `opt_${Date.now()}`, name: '', description: '', required: false, multiple_select: false, min_selections: 0, max_selections: null, choices: [] };
        return {
          ...og,
          choices: (Array.isArray(og.choices) ? og.choices : []).map(c => {
            if (!c || typeof c !== 'object') return { choice_id: `ch_${Date.now()}`, name: '', description: '', price_modifier: 0, is_default: false };
            return { ...c };
          })
        };
      }),
      // Drop-off options
      has_dropoff_option: product.has_dropoff_option || false,
      dropoff_price: product.dropoff_price || '',
      dropoff_delivery_radius: product.dropoff_delivery_radius || '',
      dropoff_minimum_order: product.dropoff_minimum_order || '',
      availability: product.availability || {
        days_of_week: [0, 1, 2, 3, 4, 5, 6],
        start_time: '08:00',
        end_time: '22:00',
        min_notice_hours: 24,
        blackout_dates: []
      }
    });
    setShowForm(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/services/products/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleToggleActive = async (product) => {
    try {
      await axios.put(`${API}/services/products/${product.service_id}`, 
        { is_active: !product.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchProducts();
      toast.success(`Product ${product.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setShowForm(false);
    setFormData({
      name: '',
      category: 'food',
      sub_category: '',
      description: '',
      short_description: '',
      pricing_model: 'flat_rate',
      base_price: 0,
      min_hours: null,
      max_hours: null,
      min_guests: null,
      max_guests: null,
      min_quantity: null,
      pricing_unit_label: '',
      supplier_commission_rate: 0.15,
      customer_markup_rate: 0.18,
      host_name: '',
      host_business_name: '',
      host_email: '',
      host_phone: '',
      service_location: '',
      service_zip_code: '',
      service_radius_miles: 25,
      covered_zip_codes: '',
      image_url: '',
      what_to_expect: '',
      whats_included: [],
      host_requirements: [],
      option_groups: [],
      // Drop-off options
      has_dropoff_option: false,
      dropoff_price: '',
      dropoff_delivery_radius: '',
      dropoff_minimum_order: '',
      availability: {
        days_of_week: [0, 1, 2, 3, 4, 5, 6],
        start_time: '08:00',
        end_time: '22:00',
        min_notice_hours: 24,
        blackout_dates: []
      }
    });
  };

  const updateAdminSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, adminSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-2">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Admin
            </Button>
            <h1 className="text-3xl font-bold">Product & Service Management</h1>
            <p className="text-muted-foreground">Manage your event services and platform settings</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="h-4 w-4" /> Products ({products.length})
            </TabsTrigger>
            <TabsTrigger value="packages" className="flex items-center gap-2" data-testid="packages-tab">
              <Layers className="h-4 w-4" /> Packages
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" /> Platform Settings
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            {!showForm ? (
              <>
                <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                  <h2 className="text-xl font-semibold">All Products</h2>
                  <Button onClick={() => setShowForm(true)} data-testid="add-product-btn">
                    <Plus className="mr-2 h-4 w-4" /> Add New Product
                  </Button>
                </div>

                {/* Search + clear */}
                <div className="relative max-w-xl mb-4" data-testid="products-search">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, description, category, sub-category, or city..."
                    className="pl-9 pr-10"
                    data-testid="products-search-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="products-search-clear"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* City/State Filter */}
                <div className="flex items-center gap-3 mb-4" data-testid="city-filter">
                  <Label className="text-sm font-semibold text-muted-foreground whitespace-nowrap">Filter by Market:</Label>
                  <div className="flex gap-2">
                    {[
                      { value: 'all', label: 'All Markets' },
                      { value: 'denver', label: 'Denver, CO' },
                      { value: 'charlotte', label: 'Charlotte, NC' },
                      { value: 'los_angeles', label: 'Los Angeles, CA' },
                    ].map(opt => (
                      <Button
                        key={opt.value}
                        size="sm"
                        variant={cityFilter === opt.value ? 'default' : 'outline'}
                        className={`text-xs h-8 ${cityFilter === opt.value ? 'bg-indigo-600' : ''}`}
                        onClick={() => setCityFilter(opt.value)}
                        data-testid={`filter-${opt.value}`}
                      >
                        {opt.label}
                        <Badge variant="outline" className="ml-1.5 text-[10px] px-1.5">
                          {opt.value === 'all' ? products.length : products.filter(p => (p.city || 'denver') === opt.value).length}
                        </Badge>
                      </Button>
                    ))}
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-12">Loading...</div>
                ) : products.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <p className="text-muted-foreground mb-4">No products yet</p>
                      <Button onClick={() => setShowForm(true)}>Create Your First Product</Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {(() => {
                      const q = (searchQuery || '').trim().toLowerCase();
                      const filtered = products
                        .filter(product => cityFilter === 'all' || (product.city || 'denver') === cityFilter)
                        .filter(product => {
                          if (!q) return true;
                          const haystack = [
                            product.name, product.short_description, product.description,
                            product.category, product.sub_category, product.city, product.slug,
                          ].filter(Boolean).join(' ').toLowerCase();
                          return haystack.includes(q);
                        });
                      if (filtered.length === 0 && q) {
                        return (
                          <Card className="text-center py-10" data-testid="search-empty-state">
                            <CardContent>
                              <p className="text-sm text-muted-foreground">No products match "<span className="font-semibold">{searchQuery}</span>".</p>
                              <Button variant="outline" size="sm" className="mt-3" onClick={() => setSearchQuery('')}>Clear search</Button>
                            </CardContent>
                          </Card>
                        );
                      }
                      return filtered.map(product => (
                      <Card key={product.service_id} className={`${!product.is_active ? 'opacity-60' : ''}`}>
                        <CardContent className="flex items-center gap-4 p-4">
                          {product.image_url && (
                            <img src={product.image_url} alt={product.name} className="w-20 h-20 rounded object-cover" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{product.name}</h3>
                              <Badge variant={product.is_active ? 'default' : 'secondary'}>
                                {product.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              {product.is_featured && <Badge className="bg-amber-500">Featured</Badge>}
                              <Badge variant="outline" className="text-[10px] capitalize">{product.city || 'denver'}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{product.short_description}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm flex-wrap">
                              <span className="font-medium text-muted-foreground" data-testid={`product-base-price-${product.service_id}`}>
                                Base: ${product.base_price}
                              </span>
                              <span className="font-medium text-emerald-600" data-testid={`product-customer-price-${product.service_id}`}>
                                Customer: ${product.customer_price || (product.base_price * (1 + (product.customer_markup_rate || 0.18))).toFixed(2)}
                                {product.pricing_unit_label && `/${product.pricing_unit_label}`}
                              </span>
                              <Badge variant="outline">{product.category}</Badge>
                              <Badge variant="outline">{product.pricing_model}</Badge>
                              {product.min_quantity && (
                                <Badge variant="outline" className="text-xs">Min: {product.min_quantity} {product.pricing_unit_label || 'units'}</Badge>
                              )}
                              <span className="text-muted-foreground">ZIP: {product.service_zip_code}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={product.is_active}
                              onCheckedChange={() => handleToggleActive(product)}
                            />
                            <Button variant="outline" size="sm" onClick={() => handleEdit(product)}>
                              Edit
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => handleDelete(product.service_id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                      ));
                    })()}
                  </div>
                )}
              </>
            ) : (
              /* Product Form */
              <Card>
                <CardHeader>
                  <CardTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</CardTitle>
                  <CardDescription>
                    Fill in all the details about this service/product
                  </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                  <CardContent className="space-y-8">
                    {/* Basic Info */}
                    <div className="space-y-4">
                      <h3 className="font-semibold border-b pb-2">Basic Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Product Name *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="category">Category *</Label>
                          <Select value={formData.category} onValueChange={(v) => handleInputChange('category', v)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ADMIN_CATEGORIES.map(cat => (
                                <SelectItem key={cat.id} value={cat.id} data-testid={`category-option-${cat.id}`}>{cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Sub-Category — auto-populated from the chosen Category, matches Event Planning Hub */}
                        <div className="space-y-2">
                          <Label htmlFor="sub_category">Sub-Category</Label>
                          {(() => {
                            const cat = ADMIN_CATEGORIES.find(c => c.id === formData.category);
                            const subs = cat?.subCategories || [];
                            if (subs.length === 0) {
                              return (
                                <Input
                                  id="sub_category"
                                  value={formData.sub_category || ''}
                                  onChange={(e) => handleInputChange('sub_category', e.target.value)}
                                  placeholder="e.g. general"
                                  data-testid="sub-category-input"
                                />
                              );
                            }
                            return (
                              <Select value={formData.sub_category || ''} onValueChange={(v) => handleInputChange('sub_category', v)}>
                                <SelectTrigger data-testid="sub-category-select">
                                  <SelectValue placeholder="Select sub-category" />
                                </SelectTrigger>
                                <SelectContent>
                                  {subs.map(sc => (
                                    <SelectItem key={sc} value={sc} data-testid={`sub-category-option-${sc}`}>
                                      {SUB_CATEGORY_LABELS[sc] || sc}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          })()}
                          <p className="text-[11px] text-muted-foreground">Determines the URL path and the filter chip on the Event Planning Hub.</p>
                        </div>

                        {formData.category === 'rental' && (
                          <div className="md:col-span-2 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 flex items-center justify-between gap-3" data-testid="rental-redirect-notice">
                            <div className="flex items-start gap-2">
                              <Package className="h-4 w-4 text-amber-500 mt-0.5" />
                              <div>
                                <p className="text-xs font-semibold">Event Rentals are managed in the Inventory system</p>
                                <p className="text-[11px] text-muted-foreground">Track real-time units on hand, damage deposits, delivery mile formulas, and per-date availability.</p>
                              </div>
                            </div>
                            <Button type="button" size="sm" variant="outline" className="text-xs flex-shrink-0" onClick={() => navigate('/admin/rentals')} data-testid="open-rental-manager-btn">
                              Open Rental Manager →
                            </Button>
                          </div>
                        )}
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="short_description">Short Description</Label>
                          <Input
                            id="short_description"
                            value={formData.short_description}
                            onChange={(e) => handleInputChange('short_description', e.target.value)}
                            placeholder="Brief description for cards"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="description">Full Description</Label>
                          <Textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            rows={4}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="image_url">Image URL</Label>
                          <Input
                            id="image_url"
                            value={formData.image_url}
                            onChange={(e) => handleInputChange('image_url', e.target.value)}
                            placeholder="https://..."
                          />
                        </div>

                        {/* What to Expect — appears under the Overview tab on the public listing */}
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="what_to_expect">What to Expect</Label>
                          <Textarea
                            id="what_to_expect"
                            value={formData.what_to_expect || ''}
                            onChange={(e) => handleInputChange('what_to_expect', e.target.value)}
                            rows={3}
                            placeholder="Describe what the customer will experience on the day of the event."
                            data-testid="what-to-expect-input"
                          />
                          <p className="text-[11px] text-muted-foreground">Shown on the public service detail page under the Overview tab.</p>
                        </div>

                        {/* What's Included — bulleted list shown on the public listing */}
                        <div className="space-y-2 md:col-span-2" data-testid="whats-included-section">
                          <div className="flex items-center justify-between">
                            <Label>What's Included</Label>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => handleInputChange('whats_included', [...(formData.whats_included || []), ''])}
                              data-testid="add-whats-included-btn"
                            >
                              + Add item
                            </Button>
                          </div>
                          <p className="text-[11px] text-muted-foreground">One item per line. Shows on the public listing under the "What's Included" tab.</p>
                          <div className="space-y-1.5">
                            {(formData.whats_included || []).length === 0 && (
                              <p className="text-xs text-muted-foreground italic">No items yet — click "Add item" to start.</p>
                            )}
                            {(formData.whats_included || []).map((item, idx) => (
                              <div key={idx} className="flex gap-1.5" data-testid={`whats-included-row-${idx}`}>
                                <Input
                                  value={item}
                                  onChange={(e) => {
                                    const next = [...(formData.whats_included || [])];
                                    next[idx] = e.target.value;
                                    handleInputChange('whats_included', next);
                                  }}
                                  placeholder="e.g. 4-hour live coverage, 2 photographers, edited gallery"
                                  data-testid={`whats-included-input-${idx}`}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive shrink-0"
                                  onClick={() => {
                                    const next = (formData.whats_included || []).filter((_, i) => i !== idx);
                                    handleInputChange('whats_included', next);
                                  }}
                                  data-testid={`remove-whats-included-${idx}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Host Requirements — bulleted list shown on the public listing */}
                        <div className="space-y-2 md:col-span-2" data-testid="host-requirements-section">
                          <div className="flex items-center justify-between">
                            <Label>Host Requirements</Label>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => handleInputChange('host_requirements', [...(formData.host_requirements || []), ''])}
                              data-testid="add-host-requirement-btn"
                            >
                              + Add requirement
                            </Button>
                          </div>
                          <p className="text-[11px] text-muted-foreground">What the host needs to provide (power, parking, table, etc.). Shows on the public listing under the "Requirements" tab.</p>
                          <div className="space-y-1.5">
                            {(formData.host_requirements || []).length === 0 && (
                              <p className="text-xs text-muted-foreground italic">No requirements yet — click "Add requirement" to start.</p>
                            )}
                            {(formData.host_requirements || []).map((item, idx) => (
                              <div key={idx} className="flex gap-1.5" data-testid={`host-requirement-row-${idx}`}>
                                <Input
                                  value={item}
                                  onChange={(e) => {
                                    const next = [...(formData.host_requirements || [])];
                                    next[idx] = e.target.value;
                                    handleInputChange('host_requirements', next);
                                  }}
                                  placeholder="e.g. 110V power outlet within 25 ft, covered area in case of rain"
                                  data-testid={`host-requirement-input-${idx}`}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive shrink-0"
                                  onClick={() => {
                                    const next = (formData.host_requirements || []).filter((_, i) => i !== idx);
                                    handleInputChange('host_requirements', next);
                                  }}
                                  data-testid={`remove-host-requirement-${idx}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Pricing */}
                    <div className="space-y-4">
                      <h3 className="font-semibold border-b pb-2">Pricing & Dual Markup</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="pricing_model">Pricing Model *</Label>
                          <Select value={formData.pricing_model} onValueChange={(v) => handleInputChange('pricing_model', v)}>
                            <SelectTrigger data-testid="pricing-model-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PRICING_MODELS.map(pm => (
                                <SelectItem key={pm.value} value={pm.value}>
                                  {pm.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            {PRICING_MODELS.find(pm => pm.value === formData.pricing_model)?.description}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="base_price">Wholesale / Base Price ($) *</Label>
                          <Input
                            id="base_price"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.base_price}
                            onChange={(e) => handleInputChange('base_price', parseFloat(e.target.value) || 0)}
                            required
                            data-testid="base-price-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            Supplier's wholesale price before markup
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Customer Price (auto)</Label>
                          <div className="h-9 px-3 flex items-center bg-muted rounded-md border text-sm font-semibold text-emerald-600" data-testid="customer-price-preview">
                            ${(formData.base_price * (1 + formData.customer_markup_rate)).toFixed(2)}
                            {formData.pricing_unit_label && <span className="ml-1 text-xs text-muted-foreground">/ {formData.pricing_unit_label}</span>}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Price shown to customers (base + {(formData.customer_markup_rate * 100).toFixed(0)}% markup)
                          </p>
                        </div>
                      </div>

                      {/* Weight-based pricing fields */}
                      {['per_oz', 'per_lb', 'per_unit'].includes(formData.pricing_model) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg border border-dashed" data-testid="weight-pricing-section">
                          <div className="space-y-2">
                            <Label htmlFor="pricing_unit_label" className="flex items-center gap-1">
                              <Scale className="h-3.5 w-3.5" /> Unit Label *
                            </Label>
                            <Input
                              id="pricing_unit_label"
                              value={formData.pricing_unit_label}
                              onChange={(e) => handleInputChange('pricing_unit_label', e.target.value)}
                              placeholder={formData.pricing_model === 'per_oz' ? 'oz' : formData.pricing_model === 'per_lb' ? 'lb' : 'tray'}
                              data-testid="pricing-unit-label-input"
                            />
                            <p className="text-xs text-muted-foreground">
                              Display label (e.g., oz, lb, tray, piece)
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="min_quantity">Minimum Quantity</Label>
                            <Input
                              id="min_quantity"
                              type="number"
                              step="0.1"
                              min="0"
                              value={formData.min_quantity || ''}
                              onChange={(e) => handleInputChange('min_quantity', parseFloat(e.target.value) || null)}
                              placeholder="e.g., 5"
                              data-testid="min-quantity-input"
                            />
                            <p className="text-xs text-muted-foreground">
                              Minimum order quantity in {formData.pricing_unit_label || 'units'}
                            </p>
                          </div>
                        </div>
                      )}

                      {(formData.pricing_model === 'per_hour' || formData.pricing_model === 'per_hour_per_person') && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="min_hours">Min Hours</Label>
                            <Input
                              id="min_hours"
                              type="number"
                              min="1"
                              value={formData.min_hours || ''}
                              onChange={(e) => handleInputChange('min_hours', parseInt(e.target.value) || null)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="max_hours">Max Hours</Label>
                            <Input
                              id="max_hours"
                              type="number"
                              min="1"
                              value={formData.max_hours || ''}
                              onChange={(e) => handleInputChange('max_hours', parseInt(e.target.value) || null)}
                            />
                          </div>
                        </div>
                      )}
                      {(formData.pricing_model === 'per_person' || formData.pricing_model === 'per_hour_per_person') && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="min_guests">Min Guests</Label>
                            <Input
                              id="min_guests"
                              type="number"
                              min="1"
                              value={formData.min_guests || ''}
                              onChange={(e) => handleInputChange('min_guests', parseInt(e.target.value) || null)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="max_guests">Max Guests</Label>
                            <Input
                              id="max_guests"
                              type="number"
                              min="1"
                              value={formData.max_guests || ''}
                              onChange={(e) => handleInputChange('max_guests', parseInt(e.target.value) || null)}
                            />
                          </div>
                        </div>
                      )}

                      {/* Dual Markup Configuration */}
                      <div className="p-4 bg-amber-500/5 rounded-lg border border-amber-500/20" data-testid="dual-markup-section">
                        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                          <Percent className="h-4 w-4 text-amber-500" />
                          Dual Markup Configuration
                        </h4>
                        <p className="text-xs text-muted-foreground mb-4">
                          Suppliers are charged a commission, and customers are charged a markup on the wholesale price.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="supplier_commission_rate">Supplier Commission (%)</Label>
                            <Input
                              id="supplier_commission_rate"
                              type="number"
                              step="0.01"
                              min="0"
                              max="1"
                              value={formData.supplier_commission_rate}
                              onChange={(e) => handleInputChange('supplier_commission_rate', parseFloat(e.target.value) || 0)}
                              data-testid="supplier-commission-input"
                            />
                            <p className="text-xs text-muted-foreground">
                              {(formData.supplier_commission_rate * 100).toFixed(0)}% commission &rarr; Supplier nets ${(formData.base_price * (1 - formData.supplier_commission_rate)).toFixed(2)}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="customer_markup_rate">Customer Markup (%)</Label>
                            <Input
                              id="customer_markup_rate"
                              type="number"
                              step="0.01"
                              min="0"
                              max="1"
                              value={formData.customer_markup_rate}
                              onChange={(e) => handleInputChange('customer_markup_rate', parseFloat(e.target.value) || 0)}
                              data-testid="customer-markup-input"
                            />
                            <p className="text-xs text-muted-foreground">
                              {(formData.customer_markup_rate * 100).toFixed(0)}% markup &rarr; Customer pays ${(formData.base_price * (1 + formData.customer_markup_rate)).toFixed(2)}
                            </p>
                          </div>
                        </div>
                        {/* Pricing Summary */}
                        <div className="mt-4 p-3 bg-background rounded-md border text-sm space-y-1" data-testid="pricing-summary">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Wholesale (Base)</span>
                            <span className="font-medium">${formData.base_price.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-amber-600">
                            <span>Supplier Commission ({(formData.supplier_commission_rate * 100).toFixed(0)}%)</span>
                            <span>-${(formData.base_price * formData.supplier_commission_rate).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-emerald-600">
                            <span>Supplier Net</span>
                            <span>${(formData.base_price * (1 - formData.supplier_commission_rate)).toFixed(2)}</span>
                          </div>
                          <div className="border-t my-1" />
                          <div className="flex justify-between text-indigo-600">
                            <span>Customer Markup ({(formData.customer_markup_rate * 100).toFixed(0)}%)</span>
                            <span>+${(formData.base_price * formData.customer_markup_rate).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between font-bold">
                            <span>Customer Price</span>
                            <span className="text-emerald-600">${(formData.base_price * (1 + formData.customer_markup_rate)).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Host Info */}
                    <div className="space-y-4">
                      <h3 className="font-semibold border-b pb-2">Host / Responsible Party</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="host_name">Contact Name *</Label>
                          <Input
                            id="host_name"
                            value={formData.host_name}
                            onChange={(e) => handleInputChange('host_name', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="host_business_name">Business Name</Label>
                          <Input
                            id="host_business_name"
                            value={formData.host_business_name}
                            onChange={(e) => handleInputChange('host_business_name', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="host_email">Email *</Label>
                          <Input
                            id="host_email"
                            type="email"
                            value={formData.host_email}
                            onChange={(e) => handleInputChange('host_email', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="host_phone">Phone</Label>
                          <Input
                            id="host_phone"
                            value={formData.host_phone}
                            onChange={(e) => handleInputChange('host_phone', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="space-y-4">
                      <h3 className="font-semibold border-b pb-2">Service Location & Coverage</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="service_location">Service Location (Address) *</Label>
                          <Input
                            id="service_location"
                            value={formData.service_location}
                            onChange={(e) => handleInputChange('service_location', e.target.value)}
                            placeholder="Where is this service delivered from?"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="service_zip_code">Home ZIP Code *</Label>
                          <Input
                            id="service_zip_code"
                            value={formData.service_zip_code}
                            onChange={(e) => handleInputChange('service_zip_code', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="service_radius_miles">Service Radius (miles)</Label>
                          <Input
                            id="service_radius_miles"
                            type="number"
                            min="1"
                            value={formData.service_radius_miles}
                            onChange={(e) => handleInputChange('service_radius_miles', parseInt(e.target.value) || 25)}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="covered_zip_codes">Covered ZIP Codes (comma-separated)</Label>
                          <Input
                            id="covered_zip_codes"
                            value={formData.covered_zip_codes}
                            onChange={(e) => handleInputChange('covered_zip_codes', e.target.value)}
                            placeholder="90001, 90002, 90003..."
                          />
                          <p className="text-xs text-muted-foreground">Leave empty to use radius-based coverage</p>
                        </div>
                      </div>
                    </div>

                    {/* Preferred Vendors (Suppliers) - only show when editing */}
                    {editingProduct && (
                    <div className="space-y-4" data-testid="preferred-vendors-section">
                      <h3 className="font-semibold border-b pb-2">Preferred Suppliers</h3>
                      <p className="text-sm text-muted-foreground">
                        Attach suppliers from the Supplier Hub as wholesale suppliers for this service. They'll be notified and asked to agree.
                      </p>
                      
                      {/* Current preferred vendors */}
                      {preferredVendors.length > 0 && (
                        <div className="space-y-2">
                          {preferredVendors.map((pv) => (
                            <div key={pv.vendor_id} className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-2">
                              <div className="flex items-center gap-3">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="text-sm font-medium">{pv.vendor_name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {pv.vendor_category} {pv.hourly_rate ? `· $${pv.hourly_rate}/hr` : ''}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant={pv.status === 'active' ? 'default' : pv.status === 'pending' ? 'secondary' : 'destructive'} className="text-xs">
                                  {pv.status}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-red-500"
                                  onClick={() => removePreferredVendor(pv.vendor_id)}
                                  data-testid={`remove-vendor-${pv.vendor_id}`}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add vendor search */}
                      <div className="space-y-2">
                        <Label>Search & Add Vendor</Label>
                        <Input
                          value={vendorSearch}
                          onChange={(e) => setVendorSearch(e.target.value)}
                          placeholder="Search vendors by name..."
                          data-testid="vendor-search-input"
                        />
                        {vendorSearch && (
                          <div className="max-h-40 overflow-y-auto border rounded-lg">
                            {availableVendors
                              .filter(v => 
                                v.business_name?.toLowerCase().includes(vendorSearch.toLowerCase()) &&
                                !preferredVendors.some(pv => pv.vendor_id === v.vendor_id)
                              )
                              .map(v => (
                                <div
                                  key={v.vendor_id}
                                  className="flex items-center justify-between px-3 py-2 hover:bg-muted cursor-pointer border-b last:border-b-0"
                                  onClick={() => { addPreferredVendor(v.vendor_id); setVendorSearch(''); }}
                                  data-testid={`add-vendor-${v.vendor_id}`}
                                >
                                  <div>
                                    <p className="text-sm font-medium">{v.business_name}</p>
                                    <p className="text-xs text-muted-foreground">{v.category} · {v.services?.join(', ')}</p>
                                  </div>
                                  <Plus className="h-4 w-4 text-muted-foreground" />
                                </div>
                              ))
                            }
                            {availableVendors.filter(v => 
                              v.business_name?.toLowerCase().includes(vendorSearch.toLowerCase()) &&
                              !preferredVendors.some(pv => pv.vendor_id === v.vendor_id)
                            ).length === 0 && (
                              <p className="text-sm text-muted-foreground text-center py-3">No matching vendors found</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {/* Drop-off Option */}
                    <div className="space-y-4">
                      <h3 className="font-semibold border-b pb-2">Drop-off Option</h3>
                      <p className="text-sm text-muted-foreground">
                        Enable this if customers can choose to have the product delivered without full service (staff, setup, etc.)
                      </p>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="has_dropoff_option"
                          checked={formData.has_dropoff_option}
                          onCheckedChange={(checked) => handleInputChange('has_dropoff_option', checked)}
                        />
                        <Label htmlFor="has_dropoff_option">Offer Drop-off Option</Label>
                      </div>
                      
                      {formData.has_dropoff_option && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                          <div className="space-y-2">
                            <Label htmlFor="dropoff_price">Drop-off Price ($) *</Label>
                            <Input
                              id="dropoff_price"
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.dropoff_price}
                              onChange={(e) => handleInputChange('dropoff_price', parseFloat(e.target.value) || '')}
                              placeholder="Flat fee for drop-off"
                            />
                            <p className="text-xs text-muted-foreground">Price for drop-off only (no service)</p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="dropoff_delivery_radius">Delivery Radius (miles) *</Label>
                            <Input
                              id="dropoff_delivery_radius"
                              type="number"
                              min="1"
                              value={formData.dropoff_delivery_radius}
                              onChange={(e) => handleInputChange('dropoff_delivery_radius', parseInt(e.target.value) || '')}
                              placeholder="e.g., 25"
                            />
                            <p className="text-xs text-muted-foreground">Max distance for drop-off delivery</p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="dropoff_minimum_order">Minimum Order ($)</Label>
                            <Input
                              id="dropoff_minimum_order"
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.dropoff_minimum_order}
                              onChange={(e) => handleInputChange('dropoff_minimum_order', parseFloat(e.target.value) || '')}
                              placeholder="Optional minimum"
                            />
                            <p className="text-xs text-muted-foreground">Minimum order for drop-off</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Availability */}
                    <div className="space-y-4">
                      <h3 className="font-semibold border-b pb-2">Availability</h3>
                      <div className="space-y-4">
                        <div>
                          <Label>Days Available</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {DAYS_OF_WEEK.map(day => (
                              <Button
                                key={day.value}
                                type="button"
                                variant={formData.availability.days_of_week.includes(day.value) ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handleDayToggle(day.value)}
                              >
                                {day.label.slice(0, 3)}
                              </Button>
                            ))}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="start_time">Start Time</Label>
                            <Input
                              id="start_time"
                              type="time"
                              value={formData.availability.start_time}
                              onChange={(e) => handleAvailabilityChange('start_time', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="end_time">End Time</Label>
                            <Input
                              id="end_time"
                              type="time"
                              value={formData.availability.end_time}
                              onChange={(e) => handleAvailabilityChange('end_time', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="min_notice_hours">Min Notice (hours)</Label>
                            <Input
                              id="min_notice_hours"
                              type="number"
                              min="0"
                              value={formData.availability.min_notice_hours}
                              onChange={(e) => handleAvailabilityChange('min_notice_hours', parseInt(e.target.value) || 0)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Eventism Calendar Integration */}
                    {providerCalendar && (
                      <div className="space-y-3 p-4 bg-indigo-500/5 border border-indigo-500/20 rounded-lg" data-testid="calendar-integration">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-sm flex items-center gap-2">
                            <Settings className="h-4 w-4 text-indigo-400" />
                            Eventism Calendar
                          </h4>
                          <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={() => window.open('/vendor', '_blank')}>
                            Open Calendar
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-center">
                          <div className="p-2 bg-card rounded-lg border border-border">
                            <p className="text-lg font-bold text-foreground">{providerCalendar.capacity_per_day || 0}</p>
                            <p className="text-[10px] text-muted-foreground">Events/Day</p>
                          </div>
                          <div className="p-2 bg-card rounded-lg border border-border">
                            <p className="text-lg font-bold text-red-400">{(providerCalendar.blocked_dates || []).length}</p>
                            <p className="text-[10px] text-muted-foreground">Blocked Dates</p>
                          </div>
                          <div className="p-2 bg-card rounded-lg border border-border">
                            <p className="text-lg font-bold text-emerald-400">{providerCalendar.google_calendar_connected ? 'Yes' : 'No'}</p>
                            <p className="text-[10px] text-muted-foreground">Google Synced</p>
                          </div>
                        </div>
                        {(providerCalendar.blocked_dates || []).length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Upcoming blocked dates:</p>
                            <div className="flex flex-wrap gap-1">
                              {(providerCalendar.blocked_dates || []).sort().filter(d => d >= new Date().toISOString().split('T')[0]).slice(0, 10).map(d => (
                                <Badge key={d} variant="outline" className="text-[10px] text-red-400 border-red-500/30">
                                  {new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        <p className="text-[10px] text-muted-foreground">
                          This service follows the Eventism Calendar. Blocked dates and capacity limits apply to all services managed by this provider. Manage blackout dates in the Supplier Hub Calendar tab.
                        </p>
                      </div>
                    )}

                    {/* Option Groups */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b pb-2">
                        <h3 className="font-semibold">Customization Options</h3>
                        <Button type="button" variant="outline" size="sm" onClick={addOptionGroup}>
                          <Plus className="mr-2 h-4 w-4" /> Add Option Group
                        </Button>
                      </div>
                      
                      {(Array.isArray(formData.option_groups) ? formData.option_groups : []).map((group, groupIndex) => (
                        <Card key={group.option_id || groupIndex} className="p-4">
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium">Option Group #{groupIndex + 1}</h4>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeOptionGroup(groupIndex)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Group Name *</Label>
                                <Input
                                  value={group.name}
                                  onChange={(e) => updateOptionGroup(groupIndex, 'name', e.target.value)}
                                  placeholder="e.g., Meat Options"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Description</Label>
                                <Input
                                  value={group.description || ''}
                                  onChange={(e) => updateOptionGroup(groupIndex, 'description', e.target.value)}
                                />
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-6">
                              <label className="flex items-center gap-2">
                                <Switch
                                  checked={group.required}
                                  onCheckedChange={(v) => updateOptionGroup(groupIndex, 'required', v)}
                                />
                                <span className="text-sm">Required</span>
                              </label>
                              <label className="flex items-center gap-2">
                                <Switch
                                  checked={group.multiple_select}
                                  onCheckedChange={(v) => updateOptionGroup(groupIndex, 'multiple_select', v)}
                                />
                                <span className="text-sm">Allow Multiple</span>
                              </label>
                            </div>
                            
                            {/* Choices */}
                            <div className="space-y-2 pl-4 border-l-2">
                              <div className="flex items-center justify-between">
                                <Label>Choices</Label>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => addChoice(groupIndex)}
                                >
                                  <Plus className="mr-1 h-3 w-3" /> Add Choice
                                </Button>
                              </div>
                              
                              {(Array.isArray(group.choices) ? group.choices : []).map((choice, choiceIndex) => (
                                <div key={choice.choice_id || choiceIndex} className="flex items-center gap-2">
                                  <Input
                                    value={choice.name}
                                    onChange={(e) => updateChoice(groupIndex, choiceIndex, 'name', e.target.value)}
                                    placeholder="Choice name"
                                    className="flex-1"
                                  />
                                  <div className="w-24">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={choice.price_modifier}
                                      onChange={(e) => updateChoice(groupIndex, choiceIndex, 'price_modifier', parseFloat(e.target.value) || 0)}
                                      placeholder="$ modifier"
                                    />
                                  </div>
                                  <label className="flex items-center gap-1 text-xs">
                                    <input
                                      type="checkbox"
                                      checked={choice.is_default}
                                      onChange={(e) => updateChoice(groupIndex, choiceIndex, 'is_default', e.target.checked)}
                                    />
                                    Default
                                  </label>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeChoice(groupIndex, choiceIndex)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                  
                  <CardFooter className="flex gap-3">
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      <Save className="mr-2 h-4 w-4" />
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </Button>
                  </CardFooter>
                </form>
              </Card>
            )}
          </TabsContent>

          {/* Packages Tab */}
          <TabsContent value="packages">
            <PackageBuilder />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Platform Settings
                </CardTitle>
                <CardDescription>Configure platform-wide settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="admin_fee">Admin Service Fee (%)</Label>
                    <Input
                      id="admin_fee"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={adminSettings.admin_service_fee_percent}
                      onChange={(e) => setAdminSettings(prev => ({
                        ...prev,
                        admin_service_fee_percent: parseFloat(e.target.value) || 0
                      }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      This fee is added to all invoices below the subtotal
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="min_notice">Min Booking Notice (hours)</Label>
                    <Input
                      id="min_notice"
                      type="number"
                      min="0"
                      value={adminSettings.min_booking_notice_hours}
                      onChange={(e) => setAdminSettings(prev => ({
                        ...prev,
                        min_booking_notice_hours: parseInt(e.target.value) || 0
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max_guests">Max Event Guests</Label>
                    <Input
                      id="max_guests"
                      type="number"
                      min="1"
                      value={adminSettings.max_event_guests}
                      onChange={(e) => setAdminSettings(prev => ({
                        ...prev,
                        max_event_guests: parseInt(e.target.value) || 1000
                      }))}
                    />
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button onClick={updateAdminSettings}>
                  <Save className="mr-2 h-4 w-4" /> Save Settings
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


export default function AdminProductManager() {
  return (
    <ProductErrorBoundary>
      <AdminProductManagerInner />
    </ProductErrorBoundary>
  );
}
